import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, useTheme, api } from "../App";
import {
    Trash2,
    ExternalLink,
    Clipboard,
    Calendar,
    User,
    Sparkles,
    PlayCircle,
    Image as ImageIcon,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import AppShell from "../components/AppShell";

const ShortlistCard = ({ post, onRemove, onCopy }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const content = post.content || "";
    const shouldTruncate = content.length > 500;
    const displayContent = shouldTruncate && !isExpanded
        ? content.substring(0, 500) + "..."
        : content;

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getMediaArray = (mediaData) => {
        if (Array.isArray(mediaData)) return mediaData;
        if (typeof mediaData === 'string') {
            try {
                const parsed = JSON.parse(mediaData);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    const media = getMediaArray(post.media);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);

    const nextMedia = (e) => {
        e.stopPropagation();
        setActiveMediaIndex((prev) => (prev + 1) % media.length);
    };

    const prevMedia = (e) => {
        e.stopPropagation();
        setActiveMediaIndex((prev) => (prev - 1 + media.length) % media.length);
    };

    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden hover:border-primary/40 transition-all duration-300 shadow-sm hover:shadow-md">
            {/* Card Header */}
            <div className="p-4 border-b border-border bg-muted/30 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-foreground leading-tight">
                            {post.author_name || "Unknown Author"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {post.author_handle ? `@${post.author_handle}` : "No handle"}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(post.created_at)}</span>
                </div>
            </div>

            {/* Media Section */}
            {media.length > 0 && (
                <div className="relative aspect-video bg-muted overflow-hidden border-b border-border group/media">
                    {media[activeMediaIndex].type === 'video' ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-black/90 text-white p-4 text-center">
                            <PlayCircle className="w-12 h-12 mb-3 text-primary" />
                            <p className="text-sm font-medium mb-2">Video Content</p>
                            <Button size="sm" variant="secondary" asChild className="h-8">
                                <a href={media[activeMediaIndex].url} target="_blank" rel="noopener noreferrer">
                                    Watch Video
                                </a>
                            </Button>
                        </div>
                    ) : (
                        <img
                            src={media[activeMediaIndex].url}
                            alt={`Post media ${activeMediaIndex + 1}`}
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                    )}

                    {media.length > 1 && (
                        <>
                            <button
                                onClick={prevMedia}
                                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/media:opacity-100 transition-opacity hover:bg-black/70"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={nextMedia}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/media:opacity-100 transition-opacity hover:bg-black/70"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                                {media.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`w-1.5 h-1.5 rounded-full transition-all ${idx === activeMediaIndex ? "bg-white w-3" : "bg-white/50"
                                            }`}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-bold flex items-center gap-1">
                        {media[activeMediaIndex].type === 'video' ? <PlayCircle className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                        {activeMediaIndex + 1} / {media.length}
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="p-5">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="text-foreground whitespace-pre-wrap leading-relaxed text-sm">
                        {displayContent}
                    </p>
                    {shouldTruncate && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="text-primary text-sm font-medium mt-2 hover:underline block"
                        >
                            {isExpanded ? "Show less" : "Read more"}
                        </button>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onCopy(post.content)}
                            className="h-8 px-3"
                        >
                            <Clipboard className="w-3.5 h-3.5 mr-2" />
                            Copy
                        </Button>
                        {post.post_url && (
                            <Button
                                variant="ghost"
                                size="sm"
                                asChild
                                className="h-8 px-3"
                            >
                                <a href={post.post_url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-3.5 h-3.5 mr-2" />
                                    Source
                                </a>
                            </Button>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(post.id)}
                        className="h-8 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

const ShortlistPage = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchShortlistedPosts = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get("/shortlist");
            setPosts(response.data.posts);
        } catch (error) {
            console.error("Failed to fetch shortlisted posts:", error);
            toast.error("Failed to load your shortlist");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchShortlistedPosts();
    }, [fetchShortlistedPosts]);

    const handleCopy = async (content) => {
        try {
            await navigator.clipboard.writeText(content);
            toast.success("Content copied for repurposing!");
        } catch (error) {
            toast.error("Failed to copy content");
        }
    };

    const handleRemove = async (postId) => {
        try {
            await api.delete(`/shortlist/${postId}`);
            setPosts((prev) => prev.filter((p) => p.id !== postId));
            toast.success("Removed from shortlist");
        } catch (error) {
            toast.error("Failed to remove post");
        }
    };

    const handleLogout = async () => {
        await logout();
        toast.success("Logged out successfully");
    };

    return (
        <AppShell activeNav="shortlist">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="mb-10 text-center md:text-left">
                    <h1 className="text-4xl font-extrabold tracking-tight text-foreground mb-3">
                        Repurposing <span className="text-primary">Vault</span>
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl">
                        A curated space for the insights and posts you've saved from LinkedIn. Use these as seeds for your next viral post.
                    </p>
                </div>

                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="bg-card rounded-2xl border border-border p-6 h-72 animate-pulse shadow-sm">
                                <div className="flex items-center gap-3 mb-6">
                                    <Skeleton className="w-10 h-10 rounded-full" />
                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-32" />
                                        <Skeleton className="h-3 w-20" />
                                    </div>
                                </div>
                                <div className="space-y-3 mb-8">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-5/6" />
                                </div>
                                <div className="flex justify-between items-center pt-6 border-t border-border">
                                    <Skeleton className="h-9 w-24 rounded-lg" />
                                    <Skeleton className="h-9 w-9 rounded-full" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-24 bg-card rounded-3xl border-2 border-dashed border-border shadow-inner">
                        <div className="w-20 h-20 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Clipboard className="w-10 h-10 text-muted-foreground opacity-50" />
                        </div>
                        <h3 className="text-2xl font-bold text-foreground mb-3">Your vault is empty</h3>
                        <p className="text-muted-foreground mb-8 max-w-sm mx-auto text-lg">
                            Save interesting posts from LinkedIn using our extension, and they'll appear here automatically.
                        </p>
                        <Button
                            onClick={() => navigate("/feed")}
                            size="lg"
                            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 px-8 py-6 rounded-xl font-semibold transition-all hover:scale-105"
                        >
                            Back to Feed
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {posts.map((post) => (
                            <ShortlistCard
                                key={post.id}
                                post={post}
                                onCopy={handleCopy}
                                onRemove={handleRemove}
                            />
                        ))}
                    </div>
                )}
            </div>
        </AppShell>
    );
};

export default ShortlistPage;
